import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  HiCheckCircle, HiChevronDown, HiChevronUp, HiArrowUpRight,
  HiPhone, HiClock, HiCpuChip, HiHandThumbUp, HiArrowTrendingUp, HiSparkles
} from 'react-icons/hi2';
import { FaLeaf, FaPiggyBank, FaGasPump } from 'react-icons/fa';
import {
  getDivisionCategories,
  getDivisionProducts
} from '../data/divisionProducts';

const benefits = [
  { title: 'Dealer-Backed Warranty', desc: 'All gensets carry official OEM dealer warranties on engine and alternator.' },
  { title: 'CPCB-IV Compliant', desc: 'Every silent generator meets Central Pollution Control Board noise and emission norms.' },
  { title: 'Fast Dispatch', desc: 'Standard installations commissioned within 7–14 business days.' },
  { title: '24/7 AMC Support', desc: 'Annual maintenance contracts with round-the-clock breakdown response.' },
  { title: 'Direct Ownership', desc: 'No third-party brokers — our own engineers handle every deployment.' },
  { title: 'Wide Capacity Range', desc: 'From 10 kVA retail stores to 3000 kVA industrial grids, we have the right unit.' },
];

const applications = [
  'Telecom Tower Sites', 'Hospitals & ICUs', 'Hotels & Hospitality', 'Data Centers', 'Manufacturing Plants',
  'Construction Sites', 'Government Offices', 'IT Parks', 'Retail Malls', 'Dairy & Cold Storage',
];

const faqs = [
  { q: 'What is the difference between a diesel and a silent generator?', a: 'Diesel generators are standard open-frame sets suited for industrial outdoor use. Silent generators have an acoustic enclosure that reduces noise to below 65 dBA, making them suitable for hospitals, hotels and residential areas.' },
  { q: 'Do you provide rental generators?', a: 'Yes. We operate a rental fleet from 20 kVA to 1000 kVA available on short-term and long-term hire with operators.' },
  { q: 'What is included in an AMC contract?', a: 'Our AMC plans include periodic servicing, oil and filter changes, load bank testing, genuine OEM spare parts, and 24/7 emergency breakdown response.' },
  { q: 'Can generators be synchronised for larger loads?', a: 'Yes. We supply and configure automatic synchronisation panels to run multiple generators in parallel for large load requirements.' },
];

export default function GeneratorSolutionsOverview() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const loadData = async () => {
    const cats = await getDivisionCategories('generator');
    const prods = await getDivisionProducts('generator');
    setCategories(cats);
    setProducts(prods);
    if (cats.length > 0) {
      setActiveTab(prev => {
        if (prev && cats.some(c => c.name === prev)) return prev;
        return cats[0].name;
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const filtered = products.filter(p => p.category === activeTab);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Generator Solutions.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Power Backup Division</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Generator <span className="text-gradient-gold">Solutions</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Diesel gensets, silent generators, and Air-cooled Generators systems — sales, rental, and AMC services.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="text-border-dark">/</span>
          <span>Divisions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Generator Solutions</span>
        </div>
      </div>

      {/* Overview */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Overview</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6 leading-tight">
              Reliable Power. Always On.
            </h2>
            <div className="space-y-3 text-sm text-body leading-relaxed">
              <p>Green Gusto is an authorised TMTL dealer and certified service partner for Cummins and Kirloskar generator ranges. We supply diesel, silent, and industrial gensets from 10 kVA to 3000 kVA for every sector.</p>
              <p>Our engineers conduct load sizing, site assessment, panel wiring, earthing, and commissioning. We back every installation with a structured AMC plan and guaranteed emergency response SLAs.</p>
            </div>
            <div className="mt-8">
              <Link to="/contact" className="btn-green rounded-full text-sm">
                Request a Quote
                <HiArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { v: '1200+', l: 'Units Installed' },
              { v: '3000 kVA', l: 'Max Capacity' },
              { v: '24/7', l: 'Service Support' },
              { v: '100%', l: 'OEM Certified' }
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5 text-center shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-accent/20 transition-all duration-300">
                <span className="text-2xl font-display font-extrabold text-primary block mb-1">{s.v}</span>
                <span className="text-[10px] text-body-muted font-bold uppercase tracking-wider">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================
          TMTL AUTHORIZED PARTNER & FEATURES SECTION
      ======================================================== */}
      <section className="bg-gradient-to-b from-white via-light/30 to-white border-y border-border py-20 relative overflow-hidden">
        {/* Background glow patterns */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col items-center">
            {/* TMTL Brand Logo */}
            <div className="mb-5 bg-white px-5 py-3 rounded-2xl border border-border/40 shadow-sm flex items-center justify-center hover:shadow-md transition-all duration-300">
              <img 
                src="/tmtl-logo.png" 
                alt="TMTL Logo" 
                className="h-10 w-auto object-contain" 
              />
            </div>
            <span className="eyebrow uppercase tracking-widest text-primary font-bold">Authorized TMTL Partner</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mt-3 mb-4">
              TMTL Power Generators
            </h2>
            <p className="text-sm text-body-muted leading-relaxed">
              Engineered by Eicher, TMTL engines represent the highest standards of reliability, fuel economy, and emission compliance.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-accent uppercase tracking-wider bg-accent/10 px-4 py-1.5 rounded-full">
              <span>Peace of Mind Guaranteed</span>
            </div>
          </div>

          {/* Top Row: Trust Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* 24x7 Support */}
            <div className="group flex flex-col items-center text-center p-8 bg-white border border-border/50 rounded-2xl hover:border-primary/30 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300">
              <div className="w-28 h-28 rounded-2xl bg-white border border-border/40 shadow-sm flex items-center justify-center p-2 mb-5 group-hover:scale-105 transition-transform duration-300">
                <img 
                  src="/1GG.png" 
                  alt="24x7 TMTL Care Logo" 
                  className="w-full h-full object-contain" 
                />
              </div>
              <h3 className="text-base font-bold text-heading mb-2">24x7 TMTL Care</h3>
              <p className="text-[11px] text-body mb-5 leading-relaxed">Dedicated round-the-clock customer care and engineer dispatch services.</p>
              <a 
                href="tel:18604258685" 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/5 hover:bg-primary text-primary hover:text-white text-xs font-extrabold tracking-wider uppercase transition-all duration-300 shadow-sm hover:shadow"
              >
                <HiPhone className="w-4 h-4" />
                1860 425 8685
              </a>
            </div>

            {/* CPCB II Certified */}
            <div className="group flex flex-col items-center text-center p-8 bg-white border border-border/50 rounded-2xl hover:border-primary/30 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300">
              <div className="w-28 h-28 rounded-2xl bg-white border border-border/40 shadow-sm flex items-center justify-center p-2 mb-5 group-hover:scale-105 transition-transform duration-300">
                <img 
                  src="/2GG.png" 
                  alt="CPCB II Certified Logo" 
                  className="w-full h-full object-contain" 
                />
              </div>
              <h3 className="text-base font-bold text-heading mb-2">CPCB II Certified</h3>
              <p className="text-[11px] text-body mb-5 leading-relaxed">Fully compliant with Central Pollution Control Board standard emission and noise norms.</p>
              <span className="text-[10px] font-bold bg-primary/10 text-primary px-3.5 py-1.5 rounded-full uppercase tracking-wider border border-primary/20">
                CPCB II Eco Compliant
              </span>
            </div>

            {/* Most Reliable */}
            <div className="group flex flex-col items-center text-center p-8 bg-white border border-border/50 rounded-2xl hover:border-primary/30 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300">
              <div className="w-28 h-28 rounded-2xl bg-white border border-border/40 shadow-sm flex items-center justify-center p-2 mb-5 group-hover:scale-105 transition-transform duration-300">
                <img 
                  src="/3GG.png" 
                  alt="Most Reliable Brand Logo" 
                  className="w-full h-full object-contain" 
                />
              </div>
              <h3 className="text-base font-bold text-heading mb-2">Most Reliable Brand</h3>
              <p className="text-[11px] text-body mb-5 leading-relaxed">Trusted by thousands of commercial and residential facilities nationwide.</p>
              <span className="text-[11px] font-medium text-body-muted italic bg-light px-4 py-1.5 rounded-xl border border-border/40">
                "You can rely on us..."
              </span>
            </div>
          </div>

          {/* Bottom Grid: 8 Core Advantages */}
          <div className="border-t border-border/60 pt-16">
            <div className="text-center mb-10">
              <h3 className="text-lg md:text-xl font-display font-extrabold text-heading">Core Engine Advantages</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Best in Class Service', desc: 'Authorized OEM service network and rapid resolution response.', icon: <HiHandThumbUp className="w-6 h-6" /> },
                { title: 'Eco Friendly', desc: 'Environmentally safe, low carbon emission compliant engines.', icon: <FaLeaf className="w-6 h-6" /> },
                { title: 'Fastest Growing Brand', desc: 'Leading the market in efficiency and reliable genset installation.', icon: <HiArrowTrendingUp className="w-6 h-6" /> },
                { title: 'Fuel Efficient', desc: 'Highly optimized combustion systems designed to save operational fuel costs.', icon: <FaGasPump className="w-5 h-5" /> },
                { title: 'Quality of Power', desc: 'Stable voltage and frequency regulation supporting sensitive loads.', icon: <HiSparkles className="w-6 h-6" /> },
                { title: 'Low Maintenance Cost', desc: 'Economical spares, long service intervals, and affordable contracts.', icon: <FaPiggyBank className="w-5 h-5" /> },
                { title: 'Round the Clock Support', desc: '24/7 technical help desk and emergency on-site engineer support.', icon: <HiClock className="w-6 h-6" /> },
                { title: 'Superior Technology', desc: 'Advanced Eicher engineering inside, guaranteeing optimal performance.', icon: <HiCpuChip className="w-6 h-6" /> }
              ].map((adv, idx) => (
                <div key={idx} className="bg-light p-7 rounded-2xl border border-border/40 hover:-translate-y-1 transition-all duration-300 flex flex-col items-start shadow-card hover:shadow-card-hover group min-h-[180px]">
                  <div className="w-11 h-11 rounded-xl bg-primary/5 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                    {adv.icon}
                  </div>
                  <h4 className="text-sm font-bold text-heading mb-2">{adv.title}</h4>
                  <p className="text-xs text-body-muted leading-relaxed">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>



        </div>
      </section>

      {/* Product Catalog */}
      {/* Product Catalog */}
      <section className="bg-light-section border-y border-border py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="eyebrow">Product Range</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Generator Categories</h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.name)}
                className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === cat.name
                    ? 'bg-primary text-white shadow-premium'
                    : 'bg-white border border-border text-body hover:border-primary/30 hover:text-primary shadow-card'
                  }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filtered.map(prod => (
              <div key={prod.id} className="bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 flex flex-col sm:flex-row group">
                <div className="w-full h-52 sm:w-56 sm:h-auto flex-shrink-0 bg-light-section overflow-hidden border-b sm:border-b-0 sm:border-r border-border flex items-center justify-center">
                  <img src={prod.image || '/Divisions/slide 1.jpg'} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <span className="badge-gold text-xs mb-2.5 w-fit">{prod.category}</span>
                    <h3 className="text-base font-bold text-heading mb-2 group-hover:text-primary transition-colors">{prod.name}</h3>
                    <p className="text-xs text-body leading-relaxed mb-4">{prod.desc}</p>
                    {prod.specs && Object.keys(prod.specs).length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {Object.entries(prod.specs).map(([k, v]) => (
                          <div key={k} className="bg-light-section rounded-lg px-2.5 py-1.5">
                            <span className="text-[9px] text-body-muted block uppercase tracking-wide truncate">{k}</span>
                            <span className="text-xs font-bold text-primary block truncate mt-0.5">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-2">
                    <Link
                      to="/contact"
                      className="btn-primary w-full justify-center py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg flex items-center gap-1.5"
                    >
                      Enquire Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Applications */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="eyebrow">Benefits</span>
            <h2 className="text-2xl font-display font-extrabold text-heading mb-8">Why Choose Green Gusto Generators?</h2>
            <div className="space-y-5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-heading mb-1">{b.title}</h4>
                    <p className="text-xs text-body leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <span className="eyebrow">Applications</span>
            <h2 className="text-2xl font-display font-extrabold text-heading mb-8">Industries We Serve</h2>
            <div className="flex flex-wrap gap-3">
              {applications.map((a, i) => (
                <span key={i} className="px-4 py-2 bg-white border border-border rounded-full text-xs font-semibold text-body hover:border-primary/30 hover:text-primary hover:shadow-card transition-all duration-200 cursor-default">
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- FAQ Centered Section ---- */}
      <section id="gen-overview-faq" className="py-24 bg-light-section border-t border-border">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="eyebrow">FAQ</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left gap-4"
                >
                  <span className="text-sm font-semibold text-heading">{faq.q}</span>
                  <span className={`flex-shrink-0 transition-colors ${openFaq === i ? 'text-accent' : 'text-body-muted'}`}>
                    {openFaq === i ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-body leading-relaxed border-t border-border pt-4">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
